import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Card, CardContent, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../context/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '24px',
    boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.03)',
    border: 'none',
    height: '100%',
}));

// Mapping from metric names to tracking API types
const METRIC_TO_TRACKING_TYPE = {
    'peso': 'weight',
    'peso corporal': 'weight',
    'weight': 'weight',
    'imc': 'bmi',
    'índice de masa corporal': 'bmi',
    'bmi': 'bmi',
    'grasa corporal': 'bodyfat',
    'porcentaje de grasa corporal': 'bodyfat',
    'bodyfat': 'bodyfat',
    '% grasa': 'bodyfat',
};

const getTrackingType = (metricName) => {
    const normalizedName = metricName?.toLowerCase().trim() || '';
    return METRIC_TO_TRACKING_TYPE[normalizedName] || null;
};

const BodyHistoryChart = ({ userMetrics = [], refreshKey = 0 }) => {
    const { authToken } = useAuth();
    const [selectedMetric, setSelectedMetric] = useState('weight');
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Build available metrics from userMetrics prop (no duplicates)
    const availableMetrics = useMemo(() => {
        const metrics = [];
        const addedTrackingTypes = new Set();

        // Add user's selected metrics
        if (userMetrics && userMetrics.length > 0) {
            userMetrics.forEach(um => {
                const metricType = um.metricType;
                if (!metricType) return;

                const name = metricType.name?.toLowerCase() || '';
                const trackingType = getTrackingType(name);

                // Skip custom metrics for now (no historical data endpoint)
                if (!trackingType) return;

                // Avoid duplicates by tracking type
                if (addedTrackingTypes.has(trackingType)) return;
                addedTrackingTypes.add(trackingType);

                // Normalize display name: "Peso Corporal" -> "Peso"
                let displayLabel = metricType.name;
                if (name === 'peso corporal') {
                    displayLabel = 'Peso';
                }

                metrics.push({
                    id: metricType._id,
                    label: displayLabel,
                    trackingType: trackingType,
                    unit: metricType.unit
                });
            });
        }

        // If no weight metric from user, add default one
        if (!addedTrackingTypes.has('weight')) {
            metrics.unshift({
                id: 'weight',
                label: 'Peso',
                trackingType: 'weight',
                unit: 'kg'
            });
        }

        return metrics;
    }, [userMetrics]);

    const handleMetricChange = (event, newMetric) => {
        if (newMetric !== null) {
            setSelectedMetric(newMetric);
        }
    };

    // Fetch data when selected metric changes or refreshKey changes
    useEffect(() => {
        const fetchMetricData = async () => {
            const metric = availableMetrics.find(m => m.id === selectedMetric || m.trackingType === selectedMetric);
            if (!metric || !metric.trackingType) {
                setChartData([]);
                return;
            }

            setLoading(true);
            try {
                const response = await fetch(
                    `${import.meta.env.VITE_API_URL}/tracking/${metric.trackingType}`,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );

                if (response.ok) {
                    const data = await response.json();

                    // Format data for chart
                    const formattedData = data
                        .slice(0, 10) // Last 10 entries
                        .reverse() // Oldest first
                        .map(entry => {
                            const dateObj = new Date(entry.date);
                            const dateLabel = dateObj.toLocaleDateString('es-ES', {
                                month: 'short',
                                day: 'numeric'
                            });

                            // Extract value based on type
                            let value;
                            if (metric.trackingType === 'weight') {
                                value = entry.weight?.$numberDecimal
                                    ? parseFloat(entry.weight.$numberDecimal)
                                    : parseFloat(entry.weight);
                            } else if (metric.trackingType === 'bmi') {
                                value = entry.bmi?.$numberDecimal
                                    ? parseFloat(entry.bmi.$numberDecimal)
                                    : parseFloat(entry.bmi);
                            } else if (metric.trackingType === 'bodyfat') {
                                value = entry.bodyFatPercentage?.$numberDecimal
                                    ? parseFloat(entry.bodyFatPercentage.$numberDecimal)
                                    : parseFloat(entry.bodyFatPercentage);
                            }

                            return {
                                date: dateLabel,
                                value: value || 0
                            };
                        });

                    setChartData(formattedData);
                } else if (response.status === 404) {
                    // No data yet
                    setChartData([]);
                }
            } catch (error) {
                console.error('Error fetching metric data:', error);
                setChartData([]);
            } finally {
                setLoading(false);
            }
        };

        if (authToken && selectedMetric) {
            fetchMetricData();
        }
    }, [authToken, selectedMetric, availableMetrics, refreshKey]);

    // Calculate change for subtitle
    const changeText = useMemo(() => {
        if (chartData.length < 2) return '';

        const first = chartData[0]?.value;
        const last = chartData[chartData.length - 1]?.value;

        if (!first || !last) return '';

        const diff = (last - first).toFixed(1);
        const metric = availableMetrics.find(m => m.id === selectedMetric || m.trackingType === selectedMetric);
        const unit = metric?.unit || '';

        if (parseFloat(diff) > 0) {
            return `+${diff}${unit} este período`;
        } else {
            return `${diff}${unit} este período`;
        }
    }, [chartData, selectedMetric, availableMetrics]);

    return (
        <StyledCard>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="700" color="#1e293b">
                            Evolución
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {changeText || 'Registra mediciones para ver tu progreso'}
                        </Typography>
                    </Box>

                    <ToggleButtonGroup
                        value={selectedMetric}
                        exclusive
                        onChange={handleMetricChange}
                        aria-label="metric"
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                borderRadius: '8px !important',
                                px: 1.5,
                                py: 0.5,
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#64748b',
                                '&.Mui-selected': {
                                    bgcolor: '#e0e7ff',
                                    color: '#667eea',
                                }
                            }
                        }}
                    >
                        {availableMetrics.map(metric => (
                            <ToggleButton
                                key={metric.id}
                                value={metric.trackingType || metric.id}
                            >
                                {metric.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {loading ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 10, fill: '#94a3b8' }}
                                dy={10}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 10, fill: '#94a3b8' }}
                                domain={['auto', 'auto']}
                            />
                            <Tooltip
                                contentStyle={{
                                    borderRadius: 12,
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#667eea"
                                strokeWidth={3}
                                dot={{ r: 4, fill: '#667eea', strokeWidth: 2, stroke: '#fff' }}
                                activeDot={{ r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#94a3b8',
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="body2">
                            Sin datos. Registra tus mediciones para ver tu evolución.
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </StyledCard>
    );
};

export default BodyHistoryChart;
